import { Euler, Matrix4, Vector3, Quaternion } from "three";
import { LngLat } from "mapbox-gl";
import proj4 from "proj4";
import PhotoSphere from "./PhotoSphere";
interface OrientationDatasetConstructor {
    url: string,
    projection?: string,
    datum?: string
    collector: string
    data_identifier?: string,
    getAccessTokenSilently?: () => Promise<string>;
}


const CSV_MAPPING = {
    NV5: {
        POS_X: { index: 2, name: 'Origin Easting[ftUS]' },
        POS_Y: { index: 3, name: 'Northing[ftUS]' },
        POS_Z: { index: 4, name: 'Height[ftUS]' },
        DIR_X: { index: 5, name: 'Direction Easting' },
        DIR_Y: { index: 6, name: 'Northing' },
        DIR_Z: { index: 7, name: 'Height' },
        UP_X: { index: 8, name: 'Up Easting' },
        UP_Y: { index: 9, name: 'Northing.1' },
        UP_Z: { index: 10, name: 'Height).1' }
    },
    LONGBEACH: {
        ROLL: { index: 5, name: 'roll[deg]' },
        PITCH: { index: 6, name: 'pitch[deg]' },
        HEADING: { index: 7, name: 'heading[deg]' },
        POS_X: { index: 8, name: 'projectedX[m]' },
        POS_Y: { index: 9, name: 'projectedY[m]' },
        POS_Z: { index: 10, name: 'projectedZ[m]' }
    },
    ARLINGTON: {
        POS_X: { index: 2, name: 'X' },
        POS_Y: { index: 3, name: 'Y' },
        POS_Z: { index: 4, name: 'Z' },
        OMEGA: { index: 5, name: 'Omega' },
        PHI: { index: 6, name: 'Phi' },
        KAPPA: { index: 7, name: 'Kappa' }
    },
    TECTONIC: {
        POS_X: { index: 2, name: 'X1' },
        POS_Y: { index: 3, name: 'Y1' },
        POS_Z: { index: 4, name: 'Z1' },
        TARGET_X: { index: 7, name: 'X2' },
        TARGET_Y: { index: 8, name: 'Y2' },
        TARGET_Z: { index: 9, name: 'Z2' },
        ROT_X: { index: 10, name: 'Omega' },
        ROT_Y: { index: 11, name: 'Phi' },
        ROT_Z: { index: 12, name: 'Kappa' }
    },
    PORTOFLONGBEACH_BACKPACK: {
        POS_X: { index: 3, name: 'pano_pos_x' },
        POS_Y: { index: 4, name: 'pano_pos_y' },
        POS_Z: { index: 5, name: 'pano_pos_z' },
        QUAT_W: { index: 6, name: 'pano_ori_w' },
        QUAT_X: { index: 7, name: 'pano_ori_x' },
        QUAT_Y: { index: 8, name: 'pano_ori_y' },
        QUAT_Z: { index: 9, name: 'pano_ori_z' }
    }
};

export default class OrientationDataset {
    url: string;
    projection: string;
    datum: string;
    collector: string;
    data_identifier?: string;
    getAccessTokenSilently?: () => Promise<string>;

    // Protected
    locations: Vector3[];
    lnglat: LngLat[];
    photoSpheres: PhotoSphere[];

    successfullyLoaded = false;

    constructor(params: OrientationDatasetConstructor) {
        // URL to the CSV file
        this.url = params.url;
        this.collector = params.collector;
        // The projection the lidar data was taken
        this.projection = params.projection ? params.projection : "";
        this.datum = params.datum || "NAD83";

        this.locations = [];
        this.lnglat = [];
        this.photoSpheres = [];
    }

    projectWorldToLngLat(coords: Vector3) {
        try {
            const new_proj = proj4(
                this.projection,
                "+proj=longlat +ellps=WGS84 +datum=WGS84 +no_defs", // Mapbox projection
                coords,
            );
            return new LngLat(new_proj.x, new_proj.y);
        } catch {
            console.error("Failed to project point:", coords);
        }

        return new LngLat(0, 0);
    }

    /**
     * @description The inverse of projectWorldToLngLat: a map position in the
     * point cloud's own coordinates, at the given height.
     * @param lngLat The map position
     * @param z The height to place it at (world units)
     * @return {Vector3 | null} The world position, or null if it can't be projected
     */
    projectLngLatToWorld(lngLat: { lng: number, lat: number }, z: number): Vector3 | null {
        try {
            const projected = proj4(
                "+proj=longlat +ellps=WGS84 +datum=WGS84 +no_defs",
                this.projection,
                [lngLat.lng, lngLat.lat],
            );
            return new Vector3(projected[0], projected[1], z);
        } catch {
            console.error("Failed to project point:", lngLat);
        }
        return null;
    }

    async fetchAndProcessCSV(url: string) {
        // Fetch the actual file using the presigned URL
        const response = await fetch(url);
        if (!response.ok) {
            throw new Error(`Failed to fetch CSV: ${response.statusText}`);
        }
        if (response.body) {
            const reader = response.body.getReader();
            const decoder = new TextDecoder('utf-8');

            let partialLine = '';
            const csvData: any = []; // Array to store the parsed data

            while (true) {
                const { value, done } = await reader.read();
                if (done) {
                    break;
                }

                const strChunk = decoder.decode(value, { stream: true });
                const lines = (partialLine + strChunk).split('\n');
                partialLine = lines.pop() as string; // Last line might be incomplete, save it for the next chunk

                lines.forEach((line) => {
                    const values = this.processLine(line);
                    if (values) {
                        csvData.push(values); // Add the parsed line to the array
                    }
                });
            }

            if (partialLine) {
                const values = this.processLine(partialLine); // Process the last partial line
                if (values) {
                    csvData.push(values);
                }
            }
            return csvData; // Return the array containing the parsed CSV data
        }
        
        throw new Error('Response body is null');
    }

    private getDelimiter(): string {
        if (['NV5', 'arlington_collector', 'longbeach_collector', 'portoflongbeach_collector', 'portoflongbeach_backpack_collector'].includes(this.collector)) {
            return ',';
        }
        return ';';
    }

    processLine(line: string) {
        return line.split(this.getDelimiter());
    }

    private computeOrientationFromVectors(position: Vector3, direction: Vector3, up: Vector3, bw_url?: boolean) {
        const eye = position;
        const target = new Vector3().addVectors(eye, direction);
        const rotationMatrix = new Matrix4().lookAt(eye, target, up);
        const euler = new Euler().setFromRotationMatrix(rotationMatrix, 'XYZ');
        

        const omega_grads = euler.x * 200 / Math.PI;
        //!HOTFIX for NV5 BW dataset - REMOVE ONCE WE GET PROPER SCANS
        const phi_grads = bw_url ?  (euler.y * 200 / Math.PI) + 90 : (euler.y * 200 / Math.PI);
        const kappa_grads = euler.z * 200 / Math.PI;

        const rotation = new Vector3(omega_grads,phi_grads, kappa_grads); 
        return { target, rotation };
    }

    private parseRow(newData: string[]): { position: Vector3, target: Vector3, rotation: Vector3 } {
        // Special-case: panorama poses CSV format (e.g., Queensway: ID,filename,timestamp,pano_pos_x,pano_pos_y,pano_pos_z,pano_ori_w,pano_ori_x,pano_ori_y,pano_ori_z,...)
        if (this.collector === 'portoflongbeach_backpack_collector' ) {
            const cols = CSV_MAPPING.PORTOFLONGBEACH_BACKPACK;
            const position = new Vector3(parseFloat(newData[cols.POS_X.index]), parseFloat(newData[cols.POS_Y.index]), parseFloat(newData[cols.POS_Z.index]));
            // quaternion in CSV is w,x,y,z
            const qw = parseFloat(newData[cols.QUAT_W.index]);
            const qx = parseFloat(newData[cols.QUAT_X.index]);
            const qy = parseFloat(newData[cols.QUAT_Y.index]);
            const qz = parseFloat(newData[cols.QUAT_Z.index]);
            const quat = new Quaternion(qx, qy, qz, qw);
            // PSOMAS note: quaternions are WXYZ and panoramas are stitched using the LEFT-facing camera
            // (left corresponds to 270deg / -90deg). To make the pano face forward, rotate +90deg yaw.
            const correction = new Quaternion().setFromAxisAngle(new Vector3(0, 0, 1), -Math.PI / 2);
            // Apply correction locally (quat * correction) to account for camera mounting offset
            const correctedQuat = quat.clone().multiply(correction);

            const direction = new Vector3(0, 1, 0).applyQuaternion(correctedQuat);
            const up = new Vector3(0, 0, 1).applyQuaternion(correctedQuat);
            return { position, ...this.computeOrientationFromVectors(position, direction, up) };
        }

        if (this.collector === 'NV5') {
            const cols = CSV_MAPPING.NV5;
            const bw_url = this.url.includes('_BW_');
            const position = new Vector3(parseFloat(newData[cols.POS_X.index]), parseFloat(newData[cols.POS_Y.index]), parseFloat(newData[cols.POS_Z.index]));
            const direction = new Vector3(parseFloat(newData[cols.DIR_X.index]), parseFloat(newData[cols.DIR_Y.index]), parseFloat(newData[cols.DIR_Z.index]));
            const up = new Vector3(parseFloat(newData[cols.UP_X.index]), parseFloat(newData[cols.UP_Y.index]), parseFloat(newData[cols.UP_Z.index]));
            return { position, ...this.computeOrientationFromVectors(position, direction, up, bw_url ) };
        }

        if (this.collector === 'longbeach_collector' || this.collector === 'portoflongbeach_collector' ) {
      
            const cols = CSV_MAPPING.LONGBEACH;
            // CSV positions are in meters in longbeach, but the Potree point cloud is in US survey feet.
            // 1 meter = 1/0.3048006096012192 US survey feet
            const mToUsFt = 1 / 0.3048006096012192;
            const position = new Vector3(
                parseFloat(newData[cols.POS_X.index]) * mToUsFt,
                parseFloat(newData[cols.POS_Y.index]) * mToUsFt,
                parseFloat(newData[cols.POS_Z.index]) * mToUsFt
            );
            const roll = parseFloat(newData[cols.ROLL.index]) * Math.PI / 180;
            const pitch = parseFloat(newData[cols.PITCH.index]) * Math.PI / 180;
            const heading = parseFloat(newData[cols.HEADING.index]) * Math.PI / 180;

            const euler = new Euler(pitch, roll, -heading, 'ZXY');
            const direction = new Vector3(0, 1, 0).applyEuler(euler);
            const up = new Vector3(0, 0, 1).applyEuler(euler);
            return { position, ...this.computeOrientationFromVectors(position, direction, up) };
        }

        if (this.collector === 'arlington_collector') {
            const cols = CSV_MAPPING.ARLINGTON;
            const position = new Vector3(parseFloat(newData[cols.POS_X.index]), parseFloat(newData[cols.POS_Y.index]), parseFloat(newData[cols.POS_Z.index]));
            
            const omega_grads = parseFloat(newData[cols.OMEGA.index]);
            const phi_grads = parseFloat(newData[cols.PHI.index]);
            const kappa_grads = parseFloat(newData[cols.KAPPA.index]);
            
            // Convert grads to degrees (existing logic preserved)
            const omega_deg = omega_grads * 360 / 400;
            const phi_deg = phi_grads * 360 / 400;
            const kappa_deg = kappa_grads * 360 / 400;
            
            const heading_rad = kappa_deg * Math.PI / 180;
            const direction = new Vector3(Math.sin(heading_rad), Math.cos(heading_rad), 0).normalize();
            const target = new Vector3().addVectors(position, direction);
            const rotation = new Vector3(omega_deg, phi_deg, kappa_deg);

            return { position, target, rotation };
        }

        // Default (Tectonic)
        const cols = CSV_MAPPING.TECTONIC;
        const position = new Vector3(parseFloat(newData[cols.POS_X.index]), parseFloat(newData[cols.POS_Y.index]), parseFloat(newData[cols.POS_Z.index]));
        const target = new Vector3(parseFloat(newData[cols.TARGET_X.index]), parseFloat(newData[cols.TARGET_Y.index]), parseFloat(newData[cols.TARGET_Z.index]));
        const rotation = new Vector3(parseFloat(newData[cols.ROT_X.index]), parseFloat(newData[cols.ROT_Y.index]), parseFloat(newData[cols.ROT_Z.index]));
        return { position, target, rotation };
    }

    private getImageUrl(newData: string[]): string {
        console.log('the newData ', newData)

        if (this.url.includes('milton_ga_processed')) {
            return `${process.env.REACT_APP_LIDAR_CDN_URL}/milton_ga_processed/panoramas/${newData[1]}`;
        } else if (this.url.includes('arlington_tx_processed')) {
            return `${process.env.REACT_APP_LIDAR_CDN_URL}/arlington_tx_processed/panoramas/${newData[0]}`;
        } else if (this.url.includes('longbeach_ca_processed_sample')) {
            return `${process.env.REACT_APP_LIDAR_CDN_URL}/longbeach_ca_processed_sample/panoramas/${newData[1]}`;
        }else if (this.url.includes('Area1-15_NAVVIS')) {
            return `${process.env.REACT_APP_LIDAR_CDN_URL}/portoflongbeach_ca_processed/panoramas_NAVVIS/${newData[10]}`;
        }  else if (this.url.includes('portoflongbeach_ca_processed')) {
            return `${process.env.REACT_APP_LIDAR_CDN_URL}/portoflongbeach_ca_processed/panoramas/${newData[1]}`;
        } else if (this.url.includes('springfield_mo_processed')) {
            return `${process.env.REACT_APP_LIDAR_CDN_URL}/springfield_mo_processed/panoramas/${newData[1]}`;
        } else if (this.url.includes('maplewood_mo_processed')) {
            return `${process.env.REACT_APP_LIDAR_CDN_URL}/maplewood_mo_processed/panoramas/${newData[1]}`;
        } else if (this.url.includes('losangeles_ca_processed')) {
            return `${process.env.REACT_APP_LIDAR_CDN_URL}/losangeles_ca_processed/panoramas/${newData[1]}`;
        }
  if (this.url.includes('florissant_mo_processed')) {
      return `${process.env.REACT_APP_LIDAR_CDN_URL}/florissant_mo_processed/panoramas/${newData[1]}`;
  }
        if (this.collector === 'NV5') {
            return this.url.slice(0, this.url.lastIndexOf("/")) + `/panoramas/${newData[1]}`;
        }

        const trackName = newData[0].split('-').slice(0, 2).join('');
        return this.url.slice(0, this.url.lastIndexOf("/")) + `/panoramas/${trackName}/${newData[0]}`;
    }

    /**
     * Load a specific dataset (point cloud, external camera orientation, etc)
     * into the AppManager. Once the dependency data has been loaded, the map
     * and three.js viewer can be properly initialized using the data.
     */
    async loadDataset(pointCloudBoundingBox?: any, pointcloud?: any) {
        this.successfullyLoaded = false;
        try {
        const url = this.url;
        await this.fetchAndProcessCSV(url).then(async (data) => {
            if (data) {                
                for (let i = 1; i < data.length; i++) {
                    const newData = data[i];
                    const { position, target, rotation } = this.parseRow(newData);
                    const image_url = this.getImageUrl(newData);
                    
                    this.locations.push(position);

                    // Longbeach CSVs already have lat/lng at indices 2 and 3
                    if (this.collector === 'longbeach_collector' || this.collector === 'portoflongbeach_collector' ) {
                        const lat = parseFloat(newData[2]);
                        const lng = parseFloat(newData[3]);
                        this.lnglat.push(new LngLat(lng, lat));
                    } else {
                        const new_proj = proj4(
                            this.projection,
                            "WGS84",
                            position
                        );
                        this.lnglat.push(new LngLat(new_proj.x, new_proj.y));
                    }
                    
                    this.photoSpheres.push(
                        new PhotoSphere(
                            image_url,
                            position,
                            target,
                            rotation,
                        ),
                    );
                }
            }
            this.successfullyLoaded = true;
        },
        ).catch((error) => {
            console.error('Error loading orientation dataset:', error);
            this.successfullyLoaded = false;
        });

        if (this.successfullyLoaded) {
            console.debug(`Successfully loaded orientation dataset. Found ${this.locations.length} valid camera points.`);
        } else {
            console.debug("Failed to find an orientation.csv, not using the map.");
        }


        console.debug(`Successfully loaded orientation dataset. Found ${this.locations.length} valid camera points.`);
    }  catch (error) {
        console.error('Error loading orientation dataset:', error);
        this.successfullyLoaded = false;
    }
} 
    

    
    getLocations() {
        return this.locations;
    }

    getLngLat() {
        return this.lnglat;
    }
}
